import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Bengali translation of en.tsx. Every number comes from `limits`; every claim
 * about who can read what follows the threat model (PLAN.md 4.12). Keep this text in step with en.tsx.
 */
export function GuideBn({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Vrelis কীভাবে কাজ করে</h1>
      <p className="lead">
        Vrelis এনক্রিপ্ট করা বার্তা, অর্থাৎ টাইম ক্যাপসুল, পাঠানোর একটি সেবা, যা পড়া যায় শুধু আপনার বেছে নেওয়া মুহূর্ত থেকে: পরীক্ষার সবাই, অথবা
        একজন মানুষ। সেই মুহূর্তের আগে কেউ ক্যাপসুল পড়তে পারে না: যাঁকে লিখেছেন তিনিও না, Vrelis যাঁরা চালান তাঁরাও না, আপনিও না।
      </p>
      <p className="warn">
        এটি একটি বন্ধ পরীক্ষা। যে ক্যাপসুল রাখতে চান তা ডাউনলোড করুন (নিচে দেখুন), এবং আপনার পুনরুদ্ধারের শব্দগুচ্ছ রেখে দিন।
      </p>

      <h2>লকের ওপর কেন ভরসা করা যায়</h2>
      <p>
        লক Vrelis-এর কোনো প্রতিশ্রুতি নয়। এটি প্রকাশিত ক্রিপ্টোগ্রাফি, সঙ্গে একটি সর্বজনীন সময়ের সংকেত, যা Vrelis নিয়ন্ত্রণ করে না। নিচে আছে কী এটিকে ধরে রাখে,
        আর এটি ব্যর্থ হতে হলে আপনাকে কী বিশ্বাস করতে হতো।
      </p>

      <h3>drand: একটি সর্বজনীন ঘড়ি, যা সময়ে স্বাক্ষর করে</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> একটি নেটওয়ার্ক, যা চালায়{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>, স্বাধীন প্রতিষ্ঠানের একটি দল (তাদের মধ্যে Cloudflare, Protocol Labs, Kudelski
          Security, EPFL ও University College London), যারা মিলে প্রতি 3 সেকেন্ডে একটি র‍্যান্ডম, স্বাক্ষরিত মান প্রকাশ করে। মানগুলো নম্বর দেওয়া: রাউন্ড 1,
          রাউন্ড 2, এভাবে, প্রতিটি একটি জানা মুহূর্তের জন্য। যে কেউ কয়েকটি সর্বজনীন রিলে থেকে এগুলো পড়তে পারেন; ভবিষ্যতের কোনোটি কেউ পড়তে পারেন না, কারণ সেটি
          এখনও তৈরিই হয়নি।
        </li>
        <li>
          প্রতিটি রাউন্ড থ্রেশহোল্ড ক্রিপ্টোগ্রাফি দিয়ে স্বাক্ষরিত (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">BLS স্বাক্ষর</Ext>): প্রতিটি প্রতিষ্ঠানের কাছে একটি গোপন চাবির একটি অংশ থাকে, আর স্বাক্ষরের জন্য
          অংশগুলোর সংখ্যাগরিষ্ঠ অংশ লাগে। কোনো একক প্রতিষ্ঠান, বা তাদের কোনো সংখ্যালঘু অংশ, সময়ের আগে কোনো রাউন্ডের স্বাক্ষর তৈরি করতে পারে না। নেটওয়ার্কটি চলছে
          2020 থেকে; আমরা যে চেইন ব্যবহার করি,{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>, চলছে 2023 থেকে।
          এর সর্বজনীন চাবি আমাদের কোডে স্থির করে রাখা, আর আমরা যে প্রতিটি সংকেত পাই তা এর সঙ্গে মিলিয়ে যাচাই করা হয়।
        </li>
        <li>
          <b>সময়-লক এনক্রিপশন</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>, Protocol Labs ও Cloudflare-এর গবেষকেরা প্রকাশ করেছেন
          2023 সালে) সেই ঘড়িকে লকে পরিণত করে: একটি বার্তা
          একটি রাউন্ড নম্বরের জন্য এনক্রিপ্ট করা হয়, আর সেই রাউন্ডের স্বাক্ষর, প্রকাশিত হওয়ার পর, একমাত্র চাবি যা এটি খোলে। drand এটি প্রকাশ না করা পর্যন্ত সেই চাবি
          পৃথিবীর কোথাও নেই।
        </li>
      </ul>

      <h3>Vrelis এটি কীভাবে ব্যবহার করে</h3>
      <ul>
        <li>
          আপনার ব্রাউজার লেখা ও আপনার ফাইলের চাবিগুলো আপনার বেছে নেওয়া সময়ের drand রাউন্ডের জন্য এনক্রিপ্ট করে; পাঠানোর আগে লেখার পাতায় রাউন্ড নম্বর দেখা যায়।
          ব্যক্তিগত ক্যাপসুল দ্বিতীয়বার প্রাপকের চাবির জন্য এনক্রিপ্ট হয়, তাই সময়ের পরে শুধু তিনিই এটি খুলতে পারেন।
        </li>
        <li>
          আমাদের সার্ভার শুধু সিল করা ফল পায় ও সংরক্ষণ করে। ক্যাপসুল খোলার মতো কোনো চাবি তার কাছে নেই: সময়ের আগে নয়, আর ব্যক্তিগত ক্যাপসুলের ক্ষেত্রে
          পরেও নয়। ডেটাবেস ফাঁস হলে সাইফারটেক্সট ও মেটাডেটা প্রকাশ পেত, কোনো বার্তা কখনো নয়।
        </li>
        <li>
          সময় এলে আপনার ব্রাউজার (বা অফলাইন খোলার সরঞ্জাম) চারটি সর্বজনীন drand রিলের একটি থেকে রাউন্ডের স্বাক্ষর আনে, স্থির সর্বজনীন চাবির
          সঙ্গে মিলিয়ে যাচাই করে, এবং ডিক্রিপ্ট করে। সেই ধাপে Vrelis-এর কোনো ভূমিকা নেই।
        </li>
        <li>
          প্রতিটি ক্যাপসুল তার লেখকের চাবি দিয়ে স্বাক্ষরিত (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>, Bitcoin
          ও <Ext href="https://nostr.com">Nostr</Ext> যে ধরনের চাবি ব্যবহার করে), তাই বদলানো ক্যাপসুল ধরা পড়ে। চাবিটি আপনার ব্রাউজারে আপনার 12টি শব্দ থেকে তৈরি হয় (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) এবং এনক্রিপ্ট না করা অবস্থায় কখনো ব্রাউজার ছেড়ে যায় না।
        </li>
      </ul>

      <h3>Vrelis উধাও হয়ে গেলে</h3>
      <ul>
        <li>
          ফরম্যাটগুলো উন্মুক্ত: একটি ক্যাপসুল হলো একটি <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">Nostr ইভেন্ট</Ext> (ব্যক্তিগতটির জন্য একটি{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">NIP-59 gift wrap</Ext>), যার বিষয়বস্তু একটি{" "}
          <Ext href="https://age-encryption.org">age</Ext> ফাইল, সঙ্গে একটি <Ext href="https://github.com/drand/tlock-js">tlock</Ext> অংশ। এই উন্মুক্ত নির্দেশিকাগুলো
          বাস্তবায়ন করে এমন যেকোনো সফটওয়্যার আপনার ক্যাপসুল খুলতে পারে; আমাদেরটি লাগবেই এমন নয়।
        </li>
        <li>
          <b>ডাউনলোড</b> ক্যাপসুলগুলো আপনার নিজের ডিস্কে রাখে, অফলাইন খোলার সরঞ্জামসহ: একটি HTML ফাইল, যা সেগুলো আপনার কম্পিউটারে খোলে, কোনো
          Vrelis সার্ভার ছাড়াই, প্রয়োজনীয় drand সংকেত এবং ব্যক্তিগত ক্যাপসুলের জন্য আপনার চাবি ব্যবহার করে। একটি সম্পূর্ণ এক্সপোর্ট vrelis.com ছাড়াই খোলা যায়, যদি
          প্রয়োজনীয় সংকেত পাওয়া যায় এবং এর জন্য দরকারি ফাইল ও চাবি আপনার কাছে থাকে।
        </li>
        <li>
          প্রয়োজনীয় ভবিষ্যৎ সংকেত তৈরির আগেই যদি drand নিজে কখনো থেমে যায়, সেই ক্যাপসুলগুলো চিরকাল লক থেকে যেতে পারে। আগে থেকে ডাউনলোড করে
          রাখলেও এই ঝুঁকি দূর হয় না। যে ক্যাপসুলের খোলার তারিখ পার হয়েছে, তারও ঠিক নিজের যাচাই করা সংকেত লাগে: সেই সংকেত আপনার
          আর্কাইভে না থাকলে বা কোনো রিলে থেকে না পাওয়া গেলে, শুধু তারিখ দিয়ে এটি অফলাইনে খোলা যায় না। সম্পূর্ণ এক্সপোর্ট ও সেগুলোর দরকারি চাবি রেখে দিন।
        </li>
      </ul>

      <h3>এটি ব্যর্থ হতে হলে আপনাকে কী বিশ্বাস করতে হতো</h3>
      <ul>
        <li>
          যে drand প্রতিষ্ঠানগুলোর সংখ্যাগরিষ্ঠ অংশ গোপনে মিলে কোনো রাউন্ড আগেভাগে স্বাক্ষর করে। তারা স্বাধীন, কয়েকটি দেশে, আর চেইনটি সর্বজনীন, তাই
          এমন স্বাক্ষর সবার কাছে দৃশ্যমান হতো। এটিই পুরো ব্যবস্থার ভরসার অনুমান।
        </li>
        <li>
          যে ওয়েব পৃষ্ঠা মিথ্যা বলে। পৃষ্ঠাটি এমন কোড যা Vrelis আপনার ব্রাউজারে পাঠায়; যে সেই কোড নিয়ন্ত্রণ করে, সে সিল হওয়ার আগে লেখা পড়তে পারত, অথবা আপনি
          আনলক করার পরে আপনার চাবি। এই কারণেই ক্যাপসুল ডাউনলোড করে অফলাইনে খোলা যায়, এই কারণেই চাবির ফিঙ্গারপ্রিন্ট দেখানো হয় যাতে আপনি Vrelis-এর
          বাইরে মিলিয়ে দেখতে পারেন, আর এই কারণেই পরীক্ষার পরে একটি ফোন অ্যাপের পরিকল্পনা আছে, যা চাবিকে ওয়েব পৃষ্ঠার বাইরে রাখবে।
        </li>
        <li>
          যে আজকের ক্রিপ্টোগ্রাফি ভেঙে গেছে। এলিপটিক-কার্ভ ক্রিপ্টোগ্রাফি, যা drand ও আপনার চাবি দুটিই ব্যবহার করে, ভবিষ্যতের{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">কোয়ান্টাম কম্পিউটার</Ext> ভেঙে ফেলতে পারে।
          যে ক্যাপসুল কয়েক দশক সিল থাকার কথা, সেগুলোর জন্য এটি একটি বাস্তব ঝুঁকি, যা পরীক্ষা সমাধান করে না।
        </li>
      </ul>

      <h2>প্রবেশ করা</h2>
      <ol>
        <li>
          অ্যাডমিন আপনার ঠিকানা অনুমোদন করে থাকলে সরাসরি সেটি দিয়ে নিবন্ধন করুন; না হলে আমন্ত্রণের লিঙ্ক খুলে তার ঠিকানা ব্যবহার করুন। সেই Google অ্যাকাউন্ট দিয়ে লগ ইন করুন (Google Vrelis-কে শুধু আপনার
          নাম ও ইমেইল জানায়), অথবা পাসওয়ার্ড দিয়ে (ঠিকানা নিশ্চিত করতে আমরা একটি লিঙ্ক মেইল করি)। পরে লগ ইনের জন্য একটি পাসকি যোগ করতে পারবেন।
        </li>
        <li>
          Vrelis আপনার ব্রাউজারে আপনার <b>চাবি</b> তৈরি করার প্রস্তাব দেয়: আপনার প্রধান স্বাক্ষর, যা আপনার পাঠানো সবকিছু স্বাক্ষর করে এবং আপনাকে পাঠানো ব্যক্তিগত ক্যাপসুল খোলে। আপনি
          এটি বাদ দিয়ে পরে অ্যাকাউন্ট, চাবি পৃষ্ঠায় তৈরি করতে পারেন; চাবি ছাড়া আপনি শুধু সর্বজনীন ক্যাপসুল পড়তে পারেন। আপনার <b>পুনরুদ্ধারের শব্দগুচ্ছ</b> (12টি শব্দ, যা
          যেকোনো ডিভাইসে চাবি আবার তৈরি করে) দেখানোর আগে আপনাকে নিশ্চিত করতে বলা হয় যে কেউ আপনার স্ক্রিন দেখতে বা রেকর্ড করতে পারছে না।
        </li>
        <li>12টি শব্দ কাগজে লিখুন। আপনাকে এর তিনটি আবার লিখতে বলা হয়, তারপর এটি আপনি কি না নিশ্চিত করতে আবার লগ ইন করতে বলা হয়: পাসওয়ার্ড বা পাসকি দিয়ে, অথবা একেবারে প্রথম চাবির জন্য Google দিয়ে।</li>
        <li>
          একটি PIN বেছে নিন (অন্তত {l.pinDigits}টি অঙ্ক) এবং একটি <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">পাসকি</Ext> তৈরি করুন (আঙুলের ছাপ,
          মুখ বা আপনার ডিভাইসের PIN)। দুটি মিলে এই ডিভাইসের চাবি
          এবং নতুন ডিভাইসে নেওয়ার জন্য Vrelis-এর রাখা কপি সুরক্ষিত করে। আপনার ব্রাউজার যদি এভাবে পাসকি ব্যবহার করতে না পারে, চাবি সেই ব্রাউজারে সুরক্ষা ছাড়া থাকে এবং
          সার্ভারে কোনো কপি থাকে না; তখন অন্য ডিভাইসে যাওয়ার একমাত্র উপায় শব্দগুচ্ছ।
        </li>
      </ol>

      <h2>আপনার পুনরুদ্ধারের শব্দগুচ্ছ</h2>
      <ul>
        <li>এটি কাগজে, নিরাপদ কোথাও রাখুন। এর ছবি তুলবেন না এবং কম্পিউটারে বা পাসওয়ার্ড ম্যানেজারে রাখবেন না।</li>
        <li>
          যার কাছে 12টি শব্দ আছে, সে-ই <b>আপনি</b>: সে আপনার হয়ে স্বাক্ষর করতে এবং আপনার ব্যক্তিগত ক্যাপসুল খুলতে পারে। শব্দগুলো ফাঁস হলে অ্যাকাউন্ট,
          চাবি পৃষ্ঠায় <b>চাবি রিসেট</b> ব্যবহার করুন: আপনি শব্দগুচ্ছ লেখেন, আবার লগ ইন করেন, আর চাবি চিরতরে প্রত্যাহার হয়; এটি দিয়ে বা এর কাছে আর কেউ পাঠাতে পারে না। আপনি জানান কবে থেকে এটি
          চুরি হয়ে থাকতে পারে, আর তখন থেকে এটি দিয়ে পাঠানো ক্যাপসুল "প্রত্যাহার করা চাবি" চিহ্নিত হয়। তারপর আপনি নতুন শব্দগুচ্ছসহ নতুন চাবি তৈরি করেন।
        </li>
        <li>
          আপনার আগের চাবিগুলো একটি <b>চাবির সংগ্রহে</b> রাখা যায়, আপনার বর্তমান চাবিতে এনক্রিপ্ট করা, যাতে পুরোনো চাবিতে পাঠানো ক্যাপসুল পুরোনো শব্দগুচ্ছ ছাড়াই খোলে।
          আপনি পুরোনো শব্দগুচ্ছ নিজেও রাখতে পারেন এবং পরে চাবির সংগ্রহে যোগ করতে পারেন।
        </li>
        <li>
          কোনো ডিভাইস হারালে: অন্য একটিতে লগ ইন করে আপনার পাসকি ও PIN (পাসকি সিঙ্ক হলে) বা আপনার শব্দগুচ্ছ ব্যবহার করুন। PIN ভুলে গেলে:
          শব্দগুচ্ছ ব্যবহার করুন। {l.pinAttempts} বার ভুল PIN দিলে সার্ভারের কপি ব্লক হয়; শব্দগুচ্ছ নতুন একটি কপি লেখে।
        </li>
        <li>
          শব্দগুচ্ছ <b>এবং</b> আপনার চাবিসহ প্রতিটি ডিভাইস হারালে আপনার ব্যক্তিগত ক্যাপসুল আর কখনো খোলা যাবে না, কারও দ্বারাই না। অ্যাডমিন আপনাকে শুধু
          একটি নতুন পরিচয় দিতে পারেন। চাবি প্রত্যাহার করতেও শব্দগুচ্ছ লাগে, তাই এটি ছাড়া অ্যাডমিন আপনার হয়ে প্রত্যাহার করেন।
        </li>
      </ul>

      <h2>ক্যাপসুল লেখা</h2>
      <ul>
        <li>
          <b>পরীক্ষার সবাই</b> (সর্বজনীন) অথবা <b>একজন সদস্য</b> (ব্যক্তিগত)। সর্বজনীন ক্যাপসুল সময়ের পরে প্রত্যেক সদস্য পড়তে পারেন; ব্যক্তিগতটি শুধু
          যাঁকে লিখেছেন তিনি, আর কপি রাখলে আপনি (শুরু থেকে চালু)।
        </li>
        <li>
          প্রতিটি ক্যাপসুলের একটি <b>বিষয়</b> লাগে। এটি কখনো এনক্রিপ্ট হয় না: যে ক্যাপসুলটি দেখে সে সঙ্গে সঙ্গে এটি পড়ে, আমাদের সার্ভারও।
        </li>
        <li>
          <b>ভূমিকা</b> ঐচ্ছিক: সর্বোচ্চ {l.introChars} অক্ষর, যা সময়ের আগেই পড়া যায়, ক্যাপসুলে ক্লিক করলে। সর্বজনীন ক্যাপসুলে
          সবাই এটি পড়েন, আমাদের সার্ভারও। ব্যক্তিগত ক্যাপসুলে শুধু প্রাপক এটি পড়েন, নিজের চাবি দিয়ে; সার্ভার এটি দেখে না। দুই ক্ষেত্রেই বার্তা ও তার
          ফাইল লক থাকে।
        </li>
        <li>
          ব্যক্তিগত ক্যাপসুল কে পাঠিয়েছেন, তা প্রাপকের চাবিতে সিল করা থাকে: নিজের চাবি দিয়ে ক্যাপসুল খোলামাত্র তিনি এটি দেখেন, সময়ের অনেক আগেই।
          সেই চাবি ছাড়া যার কাছে ক্যাপসুল আছে, সে শুধু বিষয় ও সময় দেখে। আমাদের সার্ভার জানে কোন অ্যাকাউন্ট এটি পাঠিয়েছে। এটি শুধু প্রাপককে তা জানায়, তাঁর চাবিতে এনক্রিপ্ট করে, যাতে অ্যাপ যাচাই করতে পারে যে যে চাবি ক্যাপসুল স্বাক্ষর করেছে তা সেই অ্যাকাউন্টের; না হলে নামের বদলে "অযাচাইকৃত প্রেরক" দেখায়।
        </li>
        <li>
          <b>প্রেরকের নাম লুকান</b> (ব্যক্তিগত ক্যাপসুল): প্রাপক ক্যাপসুলে ক্লিক না করা পর্যন্ত তাঁর তালিকায় আপনার নামের বদলে "গোপন প্রেরক" দেখায়, তাই
          তাঁর কাঁধের ওপর দিয়ে তাকানো কেউ দেখে না কে তাঁকে লিখছে। প্রাপক তবুও সময়ের আগেই দেখতে পারেন যে এটি আপনি।
        </li>
        <li>
          কখন খুলবে বেছে নিন: 5 মিনিটে, এক ঘণ্টায়, এক দিনে, এক সপ্তাহে, এক বছরে, অথবা 2125 সাল পর্যন্ত কোনো তারিখে। আসল খোলার সময় পাঠানোর আগে দেখানো হয়; এটি
          কয়েক সেকেন্ড পর্যন্ত নির্ভুল।
        </li>
        <li>
          লেখা: সর্বজনীন ক্যাপসুলে সর্বোচ্চ {l.publicText}, ব্যক্তিগতটিতে {l.privateText}। ফাইল: সর্বোচ্চ {l.files}টি ফাইল এবং মোট বিনামূল্যে প্ল্যানে {l.fileBytes} বা লিগ্যাসি বা স্টার্ট প্ল্যানে {l.planFileBytes}। ছবি
          ছোট করা হয় ও অবস্থানসহ মেটাডেটা মুছে যায়।
        </li>
        <li>
          <b>সিল করে পাঠান</b> ক্যাপসুল স্বাক্ষর করতে একবার আপনার PIN ও পাসকি চায়। তারপর কেউ এটি বদলাতে পারে না, আর সময়ের আগে কেউ এটি পড়তে
          পারে না।
        </li>
        <li>
          কাউকে ব্যক্তিগতভাবে লেখার আগে তাঁর চাবি মিলিয়ে দেখুন: লেখার পাতায় এর ফিঙ্গারপ্রিন্ট (16টি অক্ষর) দেখা যায়। এই ব্রাউজার শেষবার দেখার পর তাঁর চাবি বদলে
          থাকলে লেখার পাতা আপনাকে সতর্ক করে এবং অপেক্ষা করে, যতক্ষণ না আপনি Vrelis-এর বাইরে তাঁর সঙ্গে নিশ্চিত হন যে নতুন চাবিটি সত্যিই তাঁর।
        </li>
      </ul>

      <h2>পড়া</h2>
      <ul>
        <li>
          <b>সর্বজনীন বার্তা</b>: সর্বজনীন ক্যাপসুল। <b>ইনবক্স</b>: আপনার জন্য ব্যক্তিগত ক্যাপসুল। <b>পাঠানো</b>: আপনার লেখা। <b>নজরে রাখা</b>: যে সর্বজনীন ক্যাপসুলের অপেক্ষায় আছেন। কোনো
          লেখকের শুধু তাঁর ক্যাপসুল দেখতে তাঁকে অনুসরণ করুন।
        </li>
        <li>
          কোনো সদস্যকে <b>লুকান</b>: তাঁর সর্বজনীন ক্যাপসুল সব ডিভাইসে আপনার সর্বজনীন বার্তা থেকে সরে যায়; তাঁর জন্য কিছুই বদলায় না। কোনো সদস্যকে <b>ব্লক করুন</b>: তিনি আর
          আপনার ইনবক্সে ব্যক্তিগত ক্যাপসুল পাঠাতে পারেন না (পাঠানোর আগে লেখার পাতা তাঁকে জানিয়ে দেয়); আপনি তবুও তাঁকে অনুসরণ করতে ও তাঁর সর্বজনীন ক্যাপসুল পড়তে পারেন।
          তাঁর আগের ক্যাপসুল ইনবক্স, ব্লক করা অংশে চলে যায় এবং তবুও খোলে। দুটিই আছে কোনো ব্যক্তির মেনুতে এবং মানুষ পৃষ্ঠায়, কে আপনাকে অনুসরণ করে তার সঙ্গে।
          Vrelis শুধু যা পায় ও পৌঁছে দেয় তার দায় নেয়: Vrelis-এর বাইরে কেউ আপনার সর্বজনীন চাবির জন্য এনক্রিপ্ট করলে তা কেউ আটকাতে পারে না।
        </li>
        <li>
          ব্যক্তিগত ক্যাপসুল খোলার আগে কিছুই দেখায় না: <b>খুলুন</b> আপনার PIN চায়, তারপর দেখায় কে পাঠিয়েছেন ও কখন খুলবে, আর সেই
          সময় এলে লেখা।
        </li>
        <li>
          আপনার চাবি লক থাকে; যে কাজে এটি লাগে তার প্রতিটি PIN চায়। পরপর কয়েকটি ক্যাপসুল পড়তে উপরের বারে <b>… মিনিটের জন্য আনলক করুন</b> ব্যবহার
          করুন। <b>লক করুন</b> সব ব্যক্তিগত জিনিস একসঙ্গে লুকায়; পৃষ্ঠা ছেড়ে গেলেও তা-ই হয়।
        </li>
        <li>
          <a href="/account">অ্যাকাউন্ট</a>, গোপনীয়তা অংশে: "আমার তালিকায় প্রেরক দেখাবে শুধু ক্যাপসুলে ক্লিক করার পরে" প্রতিটি ব্যক্তিগত ক্যাপসুলকে নাম লুকানো প্রেরকের ক্যাপসুল হিসেবে দেখায়,
          সব ডিভাইসে।
        </li>
        <li>
          তালিকায় একবারে 50টি ক্যাপসুল দেখায়; <b>আরও দেখান</b> পরেরগুলো যোগ করে। Vrelis এগুলো ফিল্টার ও সাজায় (খোলার সময় অনুযায়ী বা কখন
          পেয়েছে অনুযায়ী), তাই ফিল্টার বা দল Vrelis-কে আপনার বেছে নেওয়া সদস্যদের id পাঠায়, কখনো দল বা তার নাম নয়। প্রেরক অনুযায়ী ইনবক্স ফিল্টার করতে
          আপনার চাবি লাগে, নাম লুকানো প্রেরকের ক্যাপসুল বাদ পড়ে, আর প্রেরক শুধু ক্লিকের পরে দেখানোর সেটিং চালু থাকলে এটি বন্ধ থাকে।
        </li>
        <li>
          <b>অনুসন্ধান</b> দেখে বিষয়, সর্বজনীন ক্যাপসুলের ভূমিকা, কে সর্বজনীন ক্যাপসুল লিখেছেন এবং আপনি কাকে পাঠিয়েছেন। এটি কখনো খোলা লেখা দেখে না
          (শুধু আপনার ডিভাইসে তা আছে), বা কে আপনাকে ব্যক্তিগত ক্যাপসুল পাঠিয়েছেন তা-ও না।
        </li>
      </ul>

      <h2>উত্তরের খাম, CC ও চিঠিপত্র</h2>
      <ul>
        <li>
          ব্যক্তিগত বার্তায় একটি <b>উত্তরের খাম</b> থাকতে পারে: আপনি প্রত্যেক প্রাপকের কাছে আপনার জন্য একটি ব্যক্তিগত উত্তর চান, আপনার চাবিতে সিল করা।
          তাঁরা আপনাকে অনুসরণ না করলেও উত্তর দিতে পারেন। আপনি বেছে নেন প্রতিটি উত্তর খুলবে আপনার ঠিক করা সময়ে (সেই সময়ের পরে কোনো উত্তর গ্রহণ
          করা হয় না) নাকি প্রাপকের বেছে নেওয়া সময়ে। ক্যাপসুল ও উত্তরের দাম আপনি আগেই দেন: প্রাপকপ্রতি 1-এর বদলে 2টি স্ট্যাম্প। প্রাপকের প্রথম উত্তরে তাঁর কোনো খরচ নেই; দ্বিতীয়টিতে তাঁর একটি স্ট্যাম্প লাগে, আর আপনি দুটিই দেখেন।
        </li>
        <li>
          <b>CC</b>: একজন সদস্যকে পাঠানো ব্যক্তিগত বার্তা একসঙ্গে আরও সদস্যের কাছে যেতে পারে, এবং তালিকার সবাই দেখেন আর কে পেয়েছেন। তালিকাটি
          প্রেরকের স্বাক্ষরসহ বার্তার ভেতরে সিল করা; আমাদের সার্ভার কখনো এটি দেখে না। CC ছাড়া দলে পাঠানো বার্তা অন্ধ থাকে: কেউ দেখেন না
          আর কে পেয়েছেন।
        </li>
        <li>
          উত্তরের খামসহ বার্তা ও সেগুলোর উত্তর থাকে <b>চিঠিপত্র</b> পৃষ্ঠায়, ইনবক্স বা পাঠানো-তে নয়: প্রতিটি
          অনুরোধের নিচে আপনি প্রত্যেক প্রাপকের উত্তর ক্রমানুসারে দেখেন, নিজ নিজ সময় পর্যন্ত লক করা, আর প্রতিটির দাম কে দিয়েছেন। উত্তর যায় শুধু প্রেরকের কাছে।
        </li>
      </ul>

      <h2>Vrelis ছাড়া ক্যাপসুল রাখা</h2>
      <ul>
        <li>
          ক্যাপসুলে <b>ডাউনলোড</b> এটিকে একটি ফাইল হিসেবে সংরক্ষণ করে; সংযুক্তি থাকলে এটি ফাইল ও অফলাইন খোলার সরঞ্জামসহ একটি .zip। কয়েকটি নির্বাচন করে একটি আর্কাইভের জন্য{" "}
          <b>নির্বাচিতগুলো ডাউনলোড করুন</b> ব্যবহার করুন ("দেখানো সব নির্বাচন করুন" দেখানো ক্যাপসুলগুলো নেয়)।
        </li>
        <li>
          অফলাইন খোলার সরঞ্জাম একটি HTML ফাইল (<a href="/account">অ্যাকাউন্ট</a>, বা .zip-এর ভেতরে)। এটি আপনার ক্যাপসুল আপনার নিজের কম্পিউটারে খোলে, ব্যক্তিগতগুলো আপনার শব্দগুচ্ছ দিয়ে। এটি
          কখনো vrelis.com-এর সঙ্গে যোগাযোগ করে না; ফাইলে খোলার সংকেত এখনও না থাকলে শুধু সর্বজনীন drand সময় সেবার কাছে তা চেয়ে নেয়।
        </li>
        <li>
          "খোলা লেখা ও ফাইলও রাখুন" খোলা লেখা ও ফাইল এনক্রিপ্ট না করে ডাউনলোডে রাখে: ফাইলটি যার কাছে থাকবে সে-ই এগুলো পড়তে পারবে।
        </li>
      </ul>

      <h2>এই ডিভাইস</h2>
      <ul>
        <li>
          <b>লক করুন</b>: মেমরি থেকে চাবি সরায়। <b>লগ আউট</b>: এই সেশন শেষ করে; চাবি এই ডিভাইসে থাকে। <b>সব ডিভাইস থেকে লগ আউট</b>:
          সব ডিভাইসে আপনার সেশন শেষ করে। <b>এই ডিভাইসের তথ্য মুছুন</b>: আপনার চাবি এবং এই ব্রাউজারে Vrelis-এর সংরক্ষিত সবকিছু মুছে দেয়।
        </li>
        <li>একটি লগ ইন {l.sessionDays} দিন চলে।</li>
      </ul>

      <h2>Vrelis কী দেখতে পারে আর কী পারে না</h2>
      <ul>
        <li>
          <b>Vrelis পড়তে পারে না</b> সময়ের আগে কোনো ক্যাপসুল, ব্যক্তিগত ক্যাপসুল কখনোই (এর কোনো চাবি তার কাছে নেই), বা আপনার চাবি, PIN বা শব্দগুচ্ছ।
        </li>
        <li>
          <b>Vrelis দেখতে পায়</b>: আপনার নাম ও ইমেইল (মালিক ও অ্যাডমিনরা ইমেইল দেখেন), কে ক্যাপসুল পাঠায় ও কাকে, কখন পাঠানো হয়েছে, কখন সর্বজনীন ক্যাপসুল
          খোলে, আকার, এবং ক্যাপসুলে ফাইল আছে কি না। সময়ের পরে সর্বজনীন ক্যাপসুল প্রত্যেক সদস্য পড়তে পারেন, তাই Vrelis-ও।
        </li>
        <li>
          চাবির সংগ্রহ ও আপনার চাবির কপি শুধু এনক্রিপ্ট করা অবস্থায় সংরক্ষিত হয়; Vrelis সেগুলো খুলতে পারে না।
        </li>
        <li>
          স্বাক্ষর প্রমাণ করে কোন চাবি ক্যাপসুলটি লিখেছে। "প্রত্যাহার করা চাবি" চিহ্নিত ক্যাপসুল এমন চাবিতে স্বাক্ষরিত, যা এর মালিক পরে চুরি হয়েছে বলে জানিয়েছেন। চাবিটি যে কোনো ব্যক্তির, তা নির্ভর করে সদস্য তালিকা এবং প্রেরক
          ক্যাপসুলে যে নাম দিয়েছেন তার ওপর। যাঁদের ব্যক্তিগতভাবে লেখেন তাঁদের সঙ্গে ফিঙ্গারপ্রিন্ট মিলিয়ে দেখুন।
        </li>
      </ul>

      <h2>প্ল্যান ও স্ট্যাম্প</h2>
      <ul>
        <li>
          একটি <b>স্ট্যাম্প</b> একজন প্রাপকের কাছে একটি ডেলিভারির দাম দেয়। আপনার নিজের কপি বিনামূল্যে। বিনামূল্যে প্ল্যান দেয় মাসে {l.freeStamps}টি স্ট্যাম্প, {l.freeStorage} স্টোরেজ এবং বার্তাপ্রতি {l.fileBytes} ফাইল; লিগ্যাসি
          (মাসে {l.legacyStamps}টি স্ট্যাম্প, {l.planStorage}, {l.planFileBytes}) প্রথম সদস্যদের দেওয়া হয়েছে; স্টার্ট দেবে মাসে {l.planStamps}টি স্ট্যাম্প। এক মাসের স্ট্যাম্প পরের মাসে যায় না, আর সেগুলো আবার আসে
          পরের মাসের প্রথম দিনে (UTC)। <b>বিলিং</b> দেখায় আপনার কী আছে, কী বাকি আর কী পরিকল্পিত।
        </li>
        <li>
          আপাতত কোনো টাকা নেওয়া হয় না এবং কিছু কেনা যায় না: প্ল্যানগুলো একটি প্রাকদর্শন, আর অ্যাডমিন হাতে একটি অনুরোধ চালু করেন। আপনার প্ল্যান বা স্ট্যাম্পে যা-ই
          হোক, আপনি আগে যা পাঠিয়েছেন তা আপনি ও যাঁদের পাঠিয়েছেন তাঁরা পড়তে পারবেন।
        </li>
        <li>
          নিজের জন্য কপি ঐচ্ছিক এবং শুরু থেকে চালু। বন্ধ করলে সেই বার্তা শুধু প্রাপক কখনো পড়তে পারবেন: আপনি নিজে পড়তে পারবেন না, খোলার পরেও না।
        </li>
      </ul>

      <h2>পরীক্ষার সীমা</h2>
      <ul>
        <li>
          দিনে সর্বোচ্চ {l.daily}টি ডেলিভারি (আপনার নিজের কপি বিনামূল্যে; দলে পাঠানো ক্যাপসুল প্রত্যেক সদস্যের জন্য একটি করে গোনা হয়)। আপলোড: 24 ঘণ্টায় {l.uploadDaily} ও {l.unusedFiles}টি অব্যবহৃত ফাইল;
          24 ঘণ্টার মধ্যে কোনো ক্যাপসুলে ব্যবহার না হওয়া ফাইল মুছে ফেলা হয়।
        </li>
        <li>Google, পাসওয়ার্ড বা পাসকি দিয়ে লগ ইন করুন। কোনো পেমেন্ট নেই, কোনো সর্বজনীন নেটওয়ার্ক নেই: ক্যাপসুল পরীক্ষার ভেতরেই থাকে।</li>
        <li>
          আগে থেকেই অ্যাকাউন্ট আছে এমন ঠিকানায় আমন্ত্রণ পাঠালে তার বদলে সেই সদস্যের কাছে যুক্ত হওয়ার অনুরোধ যায়; তিনি গ্রহণ করলে আপনারা একে অপরকে অনুসরণ করেন। দুই ক্ষেত্রেই আপনার
          স্ক্রিন একই রকম দেখায়, তাই আমন্ত্রণ দিয়ে কেউ জানতে পারে না কে সদস্য।
        </li>
      </ul>

      <h2>ডেভেলপারদের জন্য</h2>
      <p>
        সেবাগুলো <a href="/developers">Vrelis API</a>-র মাধ্যমে ক্যাপসুল পড়তে ও পাঠাতে পারে। একটি সেবা অ্যাডমিনের সার্ভিস আমন্ত্রণের মাধ্যমে নিজের অ্যাকাউন্ট
        পায়; বাকি সবার কাছে এটি সাধারণ সদস্য। এটি নিজের চাবি তৈরি করে এবং প্রতিটি ক্যাপসুল নিজেই স্বাক্ষর করে; এর API টোকেন পড়তে ও পাঠাতে পারে, কিন্তু
        চাবি বা লগ ইনের পদ্ধতি পরিচালনা করতে পারে না। অন্য যে কারও মতো আপনি একটি সেবাকে ব্লক করতে বা লুকাতে পারেন।
      </p>

      <h2>পরীক্ষার সদস্যদের কাছে আমাদের অনুরোধ</h2>
      <ol>
        <li>এক ঘণ্টায় খোলে এমন একটি সর্বজনীন ক্যাপসুল পাঠান, আর অন্য একজন সদস্যকে এক দিনে খোলে এমন একটি ব্যক্তিগত ক্যাপসুল।</li>
        <li>সর্বজনীন ক্যাপসুলগুলো খোলার পরে পড়ুন, আর একটি আগেই পড়ার চেষ্টা করুন: আপনার তা পারার কথা নয়।</li>
        <li>আপনাদের একজন: দ্বিতীয় একটি ডিভাইসে চাবি প্রস্তুত করে সেখানে ইনবক্স পড়ুন।</li>
        <li>আপনাদের একজন: অন্য একজন সদস্যকে লুকান এবং দেখুন যে তাঁর ক্যাপসুল শুধু আপনার সর্বজনীন বার্তা থেকে সরে যায়।</li>
      </ol>

      <h2>সমস্যা</h2>
      <p>
        প্রতিটি পৃষ্ঠার নিচে <b>সমস্যা জানান</b> ব্যবহার করুন, অথবা {link}-এ লিখুন। জানান আপনি কী করেছেন, কী আশা করেছিলেন আর কী ঘটেছে; একটি স্ক্রিনশট সাহায্য করে।
        সেবা, এর সীমা ও দায়িত্ব বর্ণনা করা আছে <a href="/terms">ব্যবহারের শর্তাবলি</a>-তে।
      </p>
    </>
  );
}
